import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification, Button } from 'components/ds';
import PageContainer from '../../../components/PageContainer/PageContainer';
import { pageConfigs } from '../../../configs/pageConfigs';
import { useOtp } from '../../../hooks/useOtp';
import { useSearchUser } from '../../../hooks/useSearchUser';
import {
  TwoFactorValidationCodeComponent
} from '../../../components/TwoFactorValidationCodeComponent/TwoFactorValidationCodeComponent';
import { FactorValidationWrapper } from '../ForgotPasswordOtpRequest/ForgotPasswordOtpRequest.styles';
import { OtpChannelEnum } from '../../../constantes/OtpChannelEnum';
import { useOtpContext } from '../../../context/OtpContextProvider';
import { useGetUserData } from '../../../hooks/useGetUserData';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { useSchema } from '../../../hooks/useSchema';
import { SIGNUP_TYPES } from '../../../_types/schema.types';
import { useParticipantId } from '../../../hooks/useParticipantId';
import { getNavigationDestination } from '../../../utils/getNavigationDestination';
import { useUsername } from '../../../hooks/useUsername';
import { useAuth } from '../../../../../auth/AuthProvider';
import { USER_DATA_KEY } from '../../../constantes/LocalStorageEnum';
import { RouteEnum } from '../../../constantes/RouteEnum';
import { getChannelValue } from '../../../utils/getChannelValue';

export const OtpValidation: React.FC = () => {
  const [code, setCode] = useState('');
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);
  const navigate = useNavigate();
  const {getUserData: fetchUserData, loading: userDataLoading} = useGetUserData();
  const {userData} = useSearchUser();
  const {validateOtpCode, loading: valiateCodeLoading, sendOtpRequest, otpId} = useOtp();
  const {selectedChannel, currentFlow} = useOtpContext();
  const {signupType} = useSchema();
  const {participantId} = useParticipantId();
  const {username} = useUsername();
  const {loginWithOtp} = useAuth();
  
  const isOpenFlow = signupType === SIGNUP_TYPES.OPEN;
  
  const getUserDataAndLogin = async (accessToken: string, expiresIn?: string) => {
    try {
      if (!username || !accessToken) {
        
        return false;
      }
      
      // Buscar os dados do usuário
      const response = await fetchUserData();
      // ApiLocalStorage.set('user-data', response);
      ApiLocalStorage.set(USER_DATA_KEY, response);
      
      // Fazer login automático usando o novo método do AuthProvider
      try {
        const loginResult = await loginWithOtp(username, accessToken, expiresIn, response);
        console.log('Login via OTP bem-sucedido:', {
          hasProfile: !!loginResult.profile,
          username: loginResult.profile?.username
        });
        
        return true;
      } catch (error) {
        console.error('Erro no login via OTP:', error);
        notification.error({
          message: 'Erro',
          description: 'Não foi possível fazer login automático'
        });
        
        return false;
      }
      
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      notification.error({
        message: 'Erro',
        description: 'Não foi possível buscar os dados do usuário'
      });
      
      return false;
    }
  };
  
  const handleValidateOtpCode = async () => {
    if (!otpId) {
      notification.error({
        message: 'Erro',
        description: 'OTP ID não encontrado. Por favor, solicite um novo código.'
      });
      
      return;
    }
    
    if (!participantId) {
      notification.error({
        message: 'Erro',
        description: 'Dados do usuário não encontrados. Por favor, faça login novamente.'
      });
      
      return;
    }
    
    const otpResponse = await validateOtpCode({
      otpId,
      code,
      flow: currentFlow || 'Authentication',
      generateToken: true,
      participantId,
      signUpType: signupType
    });
    
    if (otpResponse) {
      let loginSuccess = true;
      
      // **FLUXO ESPECÍFICO**: Login automático apenas no fluxo aberto
      if (isOpenFlow && loginSuccess) {
        loginSuccess = await getUserDataAndLogin(
          otpResponse.accessToken || '',
          otpResponse.expiresIn
        );
      }
      
      if (loginSuccess) {
        const destination = getNavigationDestination({
          isOpenFlow,
          currentFlow: currentFlow || ''
        });
        
        // Aguardar um pouco para garantir que o estado foi atualizado
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        navigate(destination);
      } else {
        console.error('Login não foi bem-sucedido, não navegando');
      }
    }
  };
  
  const handleResend = async () => {
    if (!participantId || !currentFlow) {
      notification.error({
        message: 'Erro',
        description: 'Dados do usuário não encontrados. Por favor, faça login novamente.'
      });
      
      return;
    }
    
    try {
      const request = {
        participantId,
        flow: currentFlow,
        channel: selectedChannel
      };
      
      console.log('Reenviando OTP com:', {
        participantId: `${participantId.substring(0, 8)}...`,
        flow: currentFlow,
        channel: selectedChannel
      });
      
      await sendOtpRequest(request);
      setIsResendDisabled(true);
    } catch (error) {
      console.error('Erro ao reenviar código:', error);
      notification.error({
        message: 'Erro ao reenviar código',
        description: 'Não foi possível reenviar o código. Por favor, tente novamente.'
      });
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 6);
    setCode(value);
  };
  
  const channelValue = getChannelValue(selectedChannel, userData);
  
  // Debug logs para identificar problemas com channelValue
  console.log('OtpValidation - Debug:', {
    selectedChannel,
    userData: userData ? {
      emailObfuscated: userData.emailObfuscated,
      phoneObfuscated: userData.phoneObfuscated,
      participantId: userData.participantId
    } : null,
    channelValue
  });
  
  const codeLength = 6;
  const isLoading = userDataLoading || valiateCodeLoading;
  
  return (
    <PageContainer
      title={pageConfigs.validateOtpResetPassword.title}
      navigateBack={pageConfigs.validateOtpResetPassword.navigateBack()}
      buttonText={pageConfigs.validateOtpResetPassword.buttonLabel}
      thereIsButton
      loading={isLoading}
      onClick={handleValidateOtpCode}
      disabled={isLoading || (code.length !== codeLength)}
    >
      <FactorValidationWrapper>
        <TwoFactorValidationCodeComponent
          code={code}
          setIsResendDisabled={setIsResendDisabled}
          isResendDisabled={isResendDisabled}
          channelValue={channelValue}
          onCodeChange={handleInputChange}
          onSubmit={handleValidateOtpCode}
          onResend={handleResend}
        />
      </FactorValidationWrapper>
    </PageContainer>
  );
};
